import { useAcknowledgeTicket, useDeleteTicket, useSetWatcher } from '@servicetim/ui/api';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  describe,
  Input,
  toast,
  useConfirm,
} from '@servicetim/ui/components';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Check,
  CheckCheck,
  Copy,
  Eye,
  EyeOff,
  FolderInput,
  LoaderCircle,
  MoreHorizontal,
  Pencil,
  Trash2,
  X,
} from 'lucide-react';
import { type KeyboardEvent, useState } from 'react';
import { PriorityBadge } from './badges';
import { formatDateTime, relativeTime } from './format';
import { useTicketContext } from './ticket-context';

const toastError = (e: unknown) => {
  const view = describe(e);
  toast.error(view.title, { description: view.detail ?? undefined });
};

/** Key, editable summary, type/priority badges, acknowledge, watch and the actions menu. */
export function TicketHeader({ onMove, onClone }: { onMove: () => void; onClone: () => void }) {
  const { ticket, project } = useTicketContext();
  return (
    <header className="space-y-3" data-slot="ticket-header">
      <nav
        aria-label="Breadcrumb"
        className="flex flex-wrap items-center gap-1 text-fg-muted text-sm"
      >
        <Link to="/queue" className="hover:text-fg hover:underline">
          Queue
        </Link>
        <span aria-hidden="true">/</span>
        <Link
          to="/projects/$projectId"
          params={{ projectId: ticket.projectId }}
          className="hover:text-fg hover:underline"
        >
          {project?.name ?? ticket.display.project_id ?? 'Project'}
        </Link>
        <span aria-hidden="true">/</span>
        <span className="font-mono text-fg-muted">{ticket.key}</span>
      </nav>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <SummaryTitle />
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <AcknowledgeButton />
          <WatchButton />
          <ActionsMenu onMove={onMove} onClone={onClone} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {ticket.display.issue_type_id ? (
          <Badge variant="secondary">{ticket.display.issue_type_id}</Badge>
        ) : null}
        <PriorityBadge label={ticket.display.priority_id} />
        {ticket.parentId ? (
          <span className="text-fg-muted">
            Child of{' '}
            <Link
              to="/tickets/$idOrKey"
              params={{ idOrKey: ticket.parentId }}
              className="font-mono text-accent hover:underline"
            >
              {ticket.display.parent_id ?? 'parent'}
            </Link>
          </span>
        ) : null}
        {ticket.acknowledgedAt ? (
          <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
            <CheckCheck className="size-4" aria-hidden="true" />
            Acknowledged by {ticket.display.acknowledged_by ?? 'someone'}{' '}
            <time dateTime={ticket.acknowledgedAt} title={formatDateTime(ticket.acknowledgedAt)}>
              {relativeTime(ticket.acknowledgedAt)}
            </time>
          </span>
        ) : null}
      </div>
    </header>
  );
}

/** The summary as an <h1>; click the pencil (or the title) to edit in place with If-Match. */
function SummaryTitle() {
  const { ticket, patch, patching } = useTicketContext();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(ticket.summary);

  const start = () => {
    setDraft(ticket.summary);
    setEditing(true);
  };
  const save = async () => {
    const next = draft.trim();
    if (next.length === 0 || next === ticket.summary) {
      setEditing(false);
      return;
    }
    try {
      const saved = await patch({ summary: next });
      if (saved) toast.success('Summary saved');
      setEditing(false);
    } catch (e) {
      toastError(e);
    }
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') void save();
    if (event.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Input
          aria-label="Summary"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          autoFocus
          maxLength={500}
          className="h-10 text-lg"
        />
        <Button
          size="icon"
          aria-label="Save summary"
          onClick={() => void save()}
          disabled={patching}
        >
          {patching ? <LoaderCircle className="animate-spin" /> : <Check />}
        </Button>
        <Button size="icon" variant="ghost" aria-label="Cancel" onClick={() => setEditing(false)}>
          <X />
        </Button>
      </div>
    );
  }
  return (
    <div className="flex min-w-0 flex-1 items-start gap-2">
      <h1 className="min-w-0 font-semibold text-2xl text-fg tracking-tight">
        <span className="mr-2 font-mono text-fg-muted">{ticket.key}</span>
        {ticket.summary}
      </h1>
      {ticket.canEdit ? (
        <Button size="icon" variant="ghost" aria-label="Edit summary" onClick={start}>
          <Pencil />
        </Button>
      ) : null}
    </div>
  );
}

function AcknowledgeButton() {
  const { ticket } = useTicketContext();
  const ack = useAcknowledgeTicket();
  if (ticket.acknowledgedAt || !(ticket.canTransition || ticket.canEdit)) return null;
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={ack.isPending}
      onClick={() =>
        ack.mutate(ticket.id, {
          onSuccess: () => toast.success('Acknowledged'),
          onError: toastError,
        })
      }
    >
      {ack.isPending ? <LoaderCircle className="animate-spin" /> : <CheckCheck />}
      Acknowledge
    </Button>
  );
}

function WatchButton() {
  const { ticket, meId } = useTicketContext();
  const setWatcher = useSetWatcher(ticket.id);
  return (
    <Button
      size="sm"
      variant={ticket.watching ? 'secondary' : 'outline'}
      aria-pressed={ticket.watching}
      disabled={setWatcher.isPending}
      onClick={() =>
        setWatcher.mutate({ userId: meId, watching: !ticket.watching }, { onError: toastError })
      }
    >
      {ticket.watching ? <EyeOff /> : <Eye />}
      {ticket.watching ? 'Watching' : 'Watch'}
    </Button>
  );
}

function ActionsMenu({ onMove, onClone }: { onMove: () => void; onClone: () => void }) {
  const { ticket } = useTicketContext();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const remove = useDeleteTicket();

  const onDelete = async () => {
    await confirm({
      title: `Delete ${ticket.key}?`,
      description:
        'Comments, attachments, links and history of this ticket are deleted with it. This cannot be undone.',
      confirmLabel: 'Delete ticket',
      destructive: true,
      onConfirm: async () => {
        await remove.mutateAsync(ticket.id);
        toast.success(`${ticket.key} deleted`);
        void navigate({ to: '/queue', search: {} });
      },
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="outline" aria-label="More actions">
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onMove}>
          <FolderInput />
          Move to another project…
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onClone}>
          <Copy />
          Clone…
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={() => void onDelete()}>
          <Trash2 />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
